import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, useTheme } from '@mui/material';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import SecondaryLoader from '../loaders/SecondaryLoader';

export default function OurHeritageHero() {
  const theme = useTheme();
  const navigate = useNavigate();
  const patternUrl = "https://brokepiggytoken.xyz/wp-content/uploads/2025/07/Circle-EmotiMaroc.png";
  const headerDesign =
    'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-7-scaled.png';
  const accent = '#b0a990';

  // Loader state
  const [loading, setLoading] = useState(false);

  // Intersection animation
  const controls = useAnimation();
  const { ref, inView } = useInView({ once: true, threshold: 0.3 });
  useEffect(() => {
    if (inView) controls.start('visible');
  }, [controls, inView]);

  const textVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { delay: 0.3, duration: 0.6 } }
  };

  const handleClick = () => {
    setLoading(true);
    setTimeout(() => {
      navigate('/products');
    }, 2000); // show loader for at least 2 seconds
  };

  return (
    <>
      <AnimatePresence>
        {loading && <SecondaryLoader key="secondary-loader" />}
      </AnimatePresence>

      <Box
        id="about"
        ref={ref}
        sx={{
          position: 'relative',
          width: '100%',
          bgcolor: 'common.black',
          color: 'common.white',
          py: { xs: 4, md: 4 },
          overflow: 'hidden',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            alignItems: 'center',
            gap: { xs: 2, md: 4 },
          }}
        >
          {/* TEXT */}
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate={controls}
            style={{ flex: 1 }}
          >
            <Box
              component="img"
              src={headerDesign}
              alt="From Marrakech with Heritage"
              sx={{
                width: { xs: 120, md: 160 },
                mb: 4,
                display: 'block',
                ml: { xs: '-16px', md: '-24px' },
              }}
            />

            {/* Accent bar */}
            <Box
              sx={{
                width: 60,
                height: 4,
                bgcolor: 'secondary.main',
                mb: 2,
                borderRadius: 2,
              }}
            />

            <Typography
              variant="h3"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                color: '#FFFFFF',
                letterSpacing: '1px',
                mb: 1.5,
              }}
            >
              Our Heritage
            </Typography>

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                color: '#FFFFFF',
                fontSize: { xs: '0.95rem', md: '1rem' },
                mb: 3,
              }}
            >
              Our family’s devotion to Picholine and Meski olives spans generations, 
              with ancient groves—many trees more than two centuries old—rooted in 
              tradition and expertise. Every season, we shape and trim each tree by hand, 
              collect olives at first light in small, select harvests, and press them cold 
              in traditional stone mills. The result is a luscious golden‑green oil, bursting 
              with deep nutty aromas and a soft, lasting warmth that reflects our enduring legacy.
            </Typography>

            <Button
              component={motion.div}
              whileHover={{ x: 5 }}
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleClick}
              sx={{
                backgroundColor: 'transparent',
                color: '#fff',
                border: `1px solid ${accent}`,
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 300,
                textTransform: 'none',
                px: 3,
                py: 1,
                fontSize: '0.75rem',
                borderRadius: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: accent,
                  color: '#000',
                },
              }}
            >
              Discover Our Olive Oil
            </Button>

            <Box
              component="img"
              src={headerDesign}
              alt="From Marrakech with Heritage"
              sx={{
                width: { xs: 120, md: 160 },
                mt: 4,
                display: 'block',
                ml: 'auto',
                mr: { xs: '-16px', md: '-24px' },
              }}
            />
          </motion.div>

          {/* DESIGN SIDE */}
          <Box
            sx={{
              flex: 1,
              width: '100%',
              height: { xs: 150, md: 350 },
              backgroundImage: `url(${patternUrl})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
            }}
          />
        </Container>
      </Box>
    </>
  );
}
